/**
 * DXF vector layers on the map (rung 3, R3-B).
 *
 * Renders the layers the user PICKED in the Plans ticket from an ingested
 * companion DXF: one plain geojson source + one line layer per picked DXF
 * layer, coordinates mapped DXF → map through the persisted registration
 * (layout fit preferred, extents-stretch fallback — see dxfRegistration.ts).
 *
 * Discipline inherited from the sharp-labels phase:
 *  - CLASSIC tiler only. `dynamic: true` geojson sources drop features at the
 *    ~30k scale (A/B-proven with both string and numeric ids) and CAD layers
 *    reach 100k+. These sources never live-update mid-drag, so there is
 *    nothing for the dynamic tiler to buy here anyway.
 *  - Layers are inserted BELOW the layout stack (grid/lines/labels) and the
 *    z-order pass in MapCanvas.ensureLayerOrder knows the `dxf-layer-line-`
 *    prefix — a prefix missing from those passes ends up UNDER a recreated
 *    orthophoto (owner-reported in production for the string lines).
 *
 * Raw layer GeoJSON is fetched once per (jobId, layer) through the asset
 * route and cached module-wide; transformed copies are rebuilt only when the
 * registration signature changes (layout transform edit, overlay drag in
 * extents mode, re-ingest).
 */

import { useEffect } from 'react';
import { useProjectStore } from '../../stores/projectStore';
import { apiFetchRaw } from '../../services/apiFetch';
import {
  extentsMapper, applyAdjust, applyDxfMatrix, adjustPivot, isIdentityAdjust,
  zoneDeltaForPoints, zoneCaptures, zonePolys,
} from '../../utils/dxfRegistration';
import { useMapStore } from '../../stores/mapStore';
import type { DxfZone } from '../../utils/dxfRegistration';
import { transformPoint } from '../../domain/layout/transform';
import type { DxfToMap } from '../../utils/dxfRegistration';
import type { Overlay, OverlayDxf } from '../../stores/projectStore';
import type { SiteLayout } from '../../domain/layout/types';
import type { Map as MapboxMap, GeoJSONSource } from 'mapbox-gl';

// Distinct on satellite imagery; indexed by DXF layer index so a layer keeps
// its color across toggles and sessions.
export const DXF_LAYER_COLORS = [
  '#00e5ff', '#ffd166', '#ef476f', '#06d6a0', '#a78bfa', '#f78c6b',
  '#4cc9f0', '#c9f24c', '#ff9f1c', '#f0abfc', '#94d2bd', '#e9d8a6',
];

export function dxfLayerColor(index: number): string {
  return DXF_LAYER_COLORS[index % DXF_LAYER_COLORS.length];
}

function sanitize(id: string): string {
  return id.replace(/[^a-z0-9_-]/gi, '_').toLowerCase();
}
export function dxfSourceId(overlayId: string, layerIndex: number): string {
  return `dxf-${sanitize(overlayId)}-${layerIndex}`;
}
export function dxfLineLayerId(overlayId: string, layerIndex: number): string {
  return `dxf-layer-line-${sanitize(overlayId)}-${layerIndex}`;
}
export function dxfFillLayerId(overlayId: string, layerIndex: number): string {
  return `dxf-layer-fill-${sanitize(overlayId)}-${layerIndex}`;
}

// Highlight of what a selected zone actually captures — the answer to "which
// elements am I about to move?". One shared source; ids are stable so the
// zone drag can translate them for live feedback.
export const DXF_HL_SRC = 'dxf-zone-highlight';
export const DXF_HL_LINE = 'dxf-zone-highlight-line';
export const DXF_HL_FILL = 'dxf-zone-highlight-fill';

// ─── Raw-GeoJSON cache (untransformed, straight from R2) ────────────────────
// Keyed by outPrefix+index — a re-ingest gets a new jobId, so a new prefix.
// Bounded: CAD layers can be tens of MB decoded; evict oldest beyond the cap.
const RAW_CACHE_MAX = 24;
const rawCache = new Map<string, Promise<GeoJSON.FeatureCollection | null>>();

function fetchRawLayer(outPrefix: string, index: number): Promise<GeoJSON.FeatureCollection | null> {
  const key = `${outPrefix}${index}`;
  const hit = rawCache.get(key);
  if (hit) return hit;
  const p = (async () => {
    const res = await apiFetchRaw(`/api/v2/assets/${outPrefix}layers/${index}.json`, { method: 'GET' });
    if (res.status === 404) return null; // text-only layer, nothing to draw
    if (!res.ok) throw new Error(`layer ${index}: HTTP ${res.status}`);
    return await res.json() as GeoJSON.FeatureCollection;
  })();
  // A failed fetch must not poison the cache — the next sync retries.
  p.catch(() => rawCache.delete(key));
  rawCache.set(key, p);
  while (rawCache.size > RAW_CACHE_MAX) {
    const oldest = rawCache.keys().next().value;
    if (oldest === undefined) break;
    rawCache.delete(oldest);
  }
  return p;
}

// ─── Registration resolution ────────────────────────────────────────────────

function effectiveLayouts(): SiteLayout[] {
  const data = useProjectStore.getState().data;
  return data.siteLayouts && data.siteLayouts.length > 0
    ? data.siteLayouts
    : (data.siteLayout ? [data.siteLayout] : []);
}

/**
 * Resolve the overlay's registration to a concrete point mapper. A layout
 * registration whose layout (or transform) has since disappeared degrades to
 * the extents fallback rather than rendering nothing.
 */
/** Staged mapping so per-FEATURE corrections can act in the right frame:
 *  DXF → raw page (fit only) → zone delta → macro adjust → map.
 *
 *  Zones are looked up in RAW page space — i.e. the drawing's own frame,
 *  before any manual whole-drawing nudge. A zone names drawing content
 *  ("this row"), so it must travel WITH the drawing when the macro adjust
 *  changes, not sit still while the content slides out from under it. */
interface StagedMapper {
  toRawPage: DxfToMap;
  applyMacro: DxfToMap;
  pageToMap: DxfToMap;
  /** Page space is only meaningful for a layout-registered DXF; the extents
   *  fallback has no page frame, so zones do not apply there. */
  hasPageSpace: boolean;
}

function resolveMapper(overlay: Overlay, dxf: OverlayDxf): StagedMapper | null {
  const reg = dxf.registration;
  if (reg?.kind === 'layout') {
    const layout = effectiveLayouts().find((l) => l.id === reg.layoutId);
    if (layout?.transform) {
      const tf = layout.transform;
      // Manual correction rides in PAGE space between the fit and the
      // layout transform, so it survives re-fits underneath it.
      const pivot = adjustPivot(dxf.extents, reg.matrix);
      const identity = isIdentityAdjust(dxf.adjust);
      return {
        toRawPage: (x, y) => applyDxfMatrix(reg.matrix, x, y),
        applyMacro: (px, py) => (identity ? [px, py] : applyAdjust(dxf.adjust, pivot, px, py)),
        pageToMap: (px, py) => transformPoint(tf, px, py),
        hasPageSpace: true,
      };
    }
  }
  if (dxf.extents) {
    const m = extentsMapper(dxf.extents, overlay.bounds);
    if (!m) return null;
    return {
      toRawPage: (x, y) => [x, y],
      applyMacro: (x, y) => [x, y],
      pageToMap: m,
      hasPageSpace: false,
    };
  }
  return null;
}

/** Everything the on-map result depends on, as a comparable string. */
function mapperSignature(
  overlay: Overlay, dxf: OverlayDxf,
  zonesForMain: DxfZone[] = dxf.zones ?? [],
  editingZone: string | null = null,
): string {
  const adj = (isIdentityAdjust(dxf.adjust) ? '' : `|A:${JSON.stringify(dxf.adjust)}`)
    + (zonesForMain.length ? `|Z:${JSON.stringify(zonesForMain)}` : '')
    + (editingZone ? `|E:${editingZone}` : '');
  const reg = dxf.registration;
  if (reg?.kind === 'layout') {
    const layout = effectiveLayouts().find((l) => l.id === reg.layoutId);
    if (layout?.transform) {
      return `L:${reg.matrix.join(',')}|${layout.transform.matrix.join(',')}${adj}`;
    }
  }
  return `E:${JSON.stringify(dxf.extents)}|${JSON.stringify(overlay.bounds)}${adj}`;
}

function transformFC(
  fc: GeoJSON.FeatureCollection,
  mapper: StagedMapper,
  zones?: DxfZone[],
  /** Feature indices lifted into the edit source — see EditSet. While a zone
   *  is being edited its captured features are rendered ONLY there, so
   *  nudging it never re-encodes this (70k-feature) collection. */
  exclude?: Set<number>,
): GeoJSON.FeatureCollection {
  const useZones = mapper.hasPageSpace && !!zones?.length;
  const convert = (ring: GeoJSON.Position[]): GeoJSON.Position[] => {
    // Raw page coordinates first — zones live in the drawing's own frame.
    const page = ring.map(([x, y]) => mapper.toRawPage(x, y));
    if (useZones) {
      const [dx, dy] = zoneDeltaForPoints(zones, page);
      if (dx !== 0 || dy !== 0) {
        return page.map(([x, y]) => {
          const [ax, ay] = mapper.applyMacro(x + dx, y + dy);
          return mapper.pageToMap(ax, ay);
        });
      }
    }
    return page.map(([x, y]) => {
      const [ax, ay] = mapper.applyMacro(x, y);
      return mapper.pageToMap(ax, ay);
    });
  };
  const out: GeoJSON.Feature[] = [];
  for (let i = 0; i < fc.features.length; i++) {
    if (exclude?.has(i)) continue;
    const f = fc.features[i];
    out.push({
      ...f,
      geometry: f.geometry.type === 'Polygon'
        ? {
            type: 'Polygon',
            coordinates: (f.geometry as GeoJSON.Polygon).coordinates.map(convert),
          } as GeoJSON.Polygon
        : {
            type: 'LineString',
            coordinates: convert((f.geometry as GeoJSON.LineString).coordinates),
          } as GeoJSON.LineString,
    });
  }
  return { type: 'FeatureCollection', features: out };
}

/**
 * The features a selected zone captures, lifted out of the big sources and
 * kept here in PAGE space so a nudge re-transforms only them.
 *
 * This is the labels/grid drag lesson applied to CAD: the cost of an edit
 * must scale with the SELECTION, not with the layer. Kalia's picked layers
 * hold ~94k features; a selection is typically a few dozen, so nudging goes
 * from re-encoding the whole collection to touching a handful.
 */
interface EditSet {
  zoneId: string;
  /** sourceId → feature indices lifted here. The originals are HIDDEN via
   *  feature-state rather than removed from the data: hiding is a paint-time
   *  flag on a few dozen features, whereas removing them means re-encoding
   *  the whole source — which is what made selection take seconds. */
  excluded: Map<string, Set<number>>;
  items: Array<{ rings: GeoJSON.Position[][]; isPolygon: boolean; props: GeoJSON.GeoJsonProperties }>;
  mapper: StagedMapper;
}

/**
 * Per-feature bounding boxes in RAW page space, so a zone scan can reject
 * almost everything with four comparisons instead of transforming every
 * point of every feature. Keyed by source + registration, since that is all
 * raw page space depends on.
 */
const bboxCache = new Map<string, { sig: string; bx: Float64Array }>();

function pageBBoxes(
  key: string, sig: string, raw: GeoJSON.FeatureCollection, mapper: StagedMapper,
): Float64Array {
  const hit = bboxCache.get(key);
  if (hit && hit.sig === sig) return hit.bx;
  const bx = new Float64Array(raw.features.length * 4);
  for (let i = 0; i < raw.features.length; i++) {
    const g = raw.features[i].geometry;
    const ring = g.type === 'Polygon'
      ? (g as GeoJSON.Polygon).coordinates[0]
      : (g as GeoJSON.LineString).coordinates;
    let minx = Infinity, miny = Infinity, maxx = -Infinity, maxy = -Infinity;
    for (const [x, y] of ring) {
      const [px, py] = mapper.toRawPage(x, y);
      if (px < minx) minx = px;
      if (px > maxx) maxx = px;
      if (py < miny) miny = py;
      if (py > maxy) maxy = py;
    }
    bx[i * 4] = minx; bx[i * 4 + 1] = miny; bx[i * 4 + 2] = maxx; bx[i * 4 + 3] = maxy;
  }
  bboxCache.set(key, { sig, bx });
  if (bboxCache.size > 24) {
    const oldest = bboxCache.keys().next().value;
    if (oldest !== undefined) bboxCache.delete(oldest);
  }
  return bx;
}

/** Bounding box of every part of a zone. */
function zoneBBox(zone: DxfZone): [number, number, number, number] {
  let minx = Infinity, miny = Infinity, maxx = -Infinity, maxy = -Infinity;
  for (const poly of zonePolys(zone)) {
    for (const [x, y] of poly) {
      if (x < minx) minx = x;
      if (x > maxx) maxx = x;
      if (y < miny) miny = y;
      if (y > maxy) maxy = y;
    }
  }
  return [minx, miny, maxx, maxy];
}

// ─── Hook ───────────────────────────────────────────────────────────────────

interface Rendered {
  sig: string; // mapper signature the on-map data was built with
  color: string | null; // active per-layer override (null = drawing colors)
  fillOpacity: number;
}

/** Paint color: user override wins outright; else the drawing's own feature
 *  color (v3+ ingests); else the per-layer palette (v1/v2 data). */
function colorExpr(override: string | null, layerIndex: number): string {
  return override
    ?? (['coalesce', ['get', 'c'], dxfLayerColor(layerIndex)] as unknown as string);
}

/** Opacity that collapses to 0 for features lifted into the edit source. */
function hiddenAware(value: number): number {
  return ['case', ['boolean', ['feature-state', 'hidden'], false], 0, value] as unknown as number;
}

export default function useDxfLayers(
  mapRef: React.RefObject<MapboxMap | null>,
  isMapReady: boolean,
) {
  useEffect(() => {
    if (!isMapReady) return;
    const map = mapRef.current;
    if (!map) return;

    // What is currently on the map, by source id.
    const rendered = new Map<string, Rendered>();
    let generation = 0;
    let disposed = false;
    let editSet: EditSet | null = null;

    /** The zone currently being edited, if any — its captured features live
     *  in the edit source, not the big ones. */
    function editingZoneId(): string | null {
      const ms = useMapStore.getState();
      return ms.dxfZoneOverlayId ? ms.dxfSelectedZoneId : null;
    }

    /** Insert point: below the layout stack and the draw layers, so a fresh
     *  add lands in its slot instead of on top of labels until the next
     *  ensureLayerOrder pass. */
    function beforeId(): string | undefined {
      try {
        const layers = map!.getStyle()?.layers ?? [];
        const above = layers.find((l) =>
          l.id.startsWith('layout-module-grid-fill-')
          || l.id.startsWith('layout-module-grid-outline-')
          || l.id.startsWith('layout-string-lines-layer-')
          || l.id.startsWith('layout-module-grid-labels-')
          || l.id.startsWith('layout-string-polarity-')
          || l.id.startsWith('layout-labels-layer-')
          || l.id.startsWith('gl-draw-'));
        return above?.id;
      } catch {
        return undefined;
      }
    }

    function removeEntry(sourceId: string) {
      try {
        for (const prefix of ['dxf-layer-line-', 'dxf-layer-fill-']) {
          const layerId = sourceId.replace(/^dxf-/, prefix);
          if (map!.getLayer(layerId)) map!.removeLayer(layerId);
        }
        if (map!.getSource(sourceId)) map!.removeSource(sourceId);
      } catch { /* map may be tearing down */ }
      rendered.delete(sourceId);
    }

    function sync() {
      if (disposed) return;
      const gen = ++generation;
      const overlays = useProjectStore.getState().data.overlays;
      const hiddenVectors = useMapStore.getState().hiddenVectorOverlayIds;

      // Desired set: ingest ready + picked layers. DELIBERATELY not gated on
      // overlay.visible — that checkbox hides the RASTER sheet, and the
      // standard use is exactly "raster off, vector layers on" (first
      // production smoke: the owner unchecked the sheet and the lines
      // vanished with it). The picked list is the vector visibility control,
      // plus the legend's own per-plan Vectors checkbox (session-only hide set
      // in mapStore) so a viewer — share visitors included — can lift the
      // linework off the map without touching the operator's picks.
      const editing = editingZoneId();
      const desired = new Map<string, { overlay: Overlay; dxf: OverlayDxf; index: number; sig: string; color: string | null; fillOpacity: number; zonesForMain: DxfZone[] }>();
      for (const overlay of overlays) {
        const dxf = overlay.dxf;
        if (dxf?.status !== 'ready' || !dxf.picked?.length) continue;
        if (hiddenVectors.includes(overlay.id)) continue;
        // The edited zone's own delta is rendered by the edit source, so it
        // is deliberately absent from the big sources' signature — that is
        // what keeps a nudge from re-encoding them.
        const zonesForMain = (dxf.zones ?? []).filter((z) => z.id !== editing);
        const sig = mapperSignature(overlay, dxf, zonesForMain, editing);
        for (const index of dxf.picked) {
          const sourceId = dxfSourceId(overlay.id, index);
          desired.set(sourceId, {
            overlay, dxf, index, sig,
            color: dxf.colorOverrides?.[index] ?? null,
            fillOpacity: dxf.fillOpacity ?? 0.35,
            zonesForMain,
          });
        }
      }

      // Remove what is no longer wanted.
      for (const sourceId of [...rendered.keys()]) {
        if (!desired.has(sourceId)) removeEntry(sourceId);
      }

      // Add / retransform the rest.
      for (const [sourceId, want] of desired) {
        const cur = rendered.get(sourceId);
        if (cur && cur.sig === want.sig) {
          // Same data, maybe new paint (color override / fill opacity) —
          // paint-only fast path.
          if (cur.color !== want.color || cur.fillOpacity !== want.fillOpacity) {
            try {
              const expr = colorExpr(want.color, want.index);
              map!.setPaintProperty(dxfLineLayerId(want.overlay.id, want.index), 'line-color', expr);
              map!.setPaintProperty(dxfFillLayerId(want.overlay.id, want.index), 'fill-color', expr);
              map!.setPaintProperty(dxfFillLayerId(want.overlay.id, want.index), 'fill-opacity', hiddenAware(want.fillOpacity));
              rendered.set(sourceId, { sig: cur.sig, color: want.color, fillOpacity: want.fillOpacity });
            } catch { /* layer mid-teardown */ }
          }
          continue;
        }
        const mapper = resolveMapper(want.overlay, want.dxf);
        if (!mapper) continue; // nothing to register against (yet)
        fetchRawLayer(want.dxf.outPrefix, want.index).then((raw) => {
          if (disposed || gen !== generation) return; // superseded sync
          if (!raw) return; // text-only layer
          const data = transformFC(raw, mapper, want.zonesForMain);
          try {
            const existing = map!.getSource(sourceId) as GeoJSONSource | undefined;
            if (existing) {
              existing.setData(data);
              // setData drops feature state; a selection held across a
              // rebuild must stay hidden or it would double-draw.
              if (editSet?.excluded.has(sourceId)) applyHiddenStates(editSet);
            } else {
              // generateId gives each feature an id equal to its index, which
              // is what lets a selection be hidden by feature-state instead
              // of by re-encoding the source.
              map!.addSource(sourceId, { type: 'geojson', data, generateId: true });
              const color = colorExpr(want.color, want.index);
              const anchor = beforeId();
              // Fill first, line second at the same anchor → lines end up
              // above their fills.
              map!.addLayer({
                id: dxfFillLayerId(want.overlay.id, want.index),
                type: 'fill',
                source: sourceId,
                filter: ['==', ['get', 'f'], 1],
                paint: {
                  'fill-color': color,
                  'fill-opacity': hiddenAware(want.fillOpacity),
                },
              }, anchor);
              map!.addLayer({
                id: dxfLineLayerId(want.overlay.id, want.index),
                type: 'line',
                source: sourceId,
                filter: ['!=', ['get', 'f'], 1],
                layout: { 'line-cap': 'round', 'line-join': 'round' },
                paint: {
                  'line-color': color,
                  'line-opacity': hiddenAware(0.9),
                  'line-width': [
                    'interpolate', ['linear'], ['zoom'],
                    12, 0.7, 16, 1.4, 19, 2.2,
                  ],
                },
              }, anchor);
            }
            rendered.set(sourceId, { sig: want.sig, color: want.color, fillOpacity: want.fillOpacity });
          } catch (err) {
            console.warn('[DxfLayers] add failed:', (err as Error).message);
          }
        }).catch((err) => {
          console.warn('[DxfLayers] layer fetch failed:', (err as Error).message);
        });
      }
    }

    /**
     * Show exactly what the selected zone captures. Same containment rule as
     * the renderer, so what lights up is precisely what will move — a zone
     * outline alone can't tell you whether that long table is included.
     */
    async function syncHighlight() {
      if (disposed) return;
      const empty: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };
      const write = (data: GeoJSON.FeatureCollection) => {
        try {
          const src = map!.getSource(DXF_HL_SRC) as GeoJSONSource | undefined;
          if (src) { src.setData(data); return; }
          map!.addSource(DXF_HL_SRC, { type: 'geojson', data });
          map!.addLayer({
            id: DXF_HL_FILL, type: 'fill', source: DXF_HL_SRC,
            filter: ['==', ['get', 'f'], 1],
            paint: { 'fill-color': '#13d6c6', 'fill-opacity': 0.35 },
          });
          map!.addLayer({
            id: DXF_HL_LINE, type: 'line', source: DXF_HL_SRC,
            filter: ['!=', ['get', 'f'], 1],
            layout: { 'line-cap': 'round', 'line-join': 'round' },
            paint: { 'line-color': '#13d6c6', 'line-width': 3, 'line-opacity': 0.95 },
          });
        } catch { /* style mid-teardown */ }
      };

      const zoneId = editingZoneId();
      const overlayId = useMapStore.getState().dxfZoneOverlayId;
      const st = useProjectStore.getState();
      const overlay = overlayId ? st.data.overlays.find((o) => o.id === overlayId) : undefined;
      const dxf = overlay?.dxf;
      const zone = zoneId ? dxf?.zones?.find((z) => z.id === zoneId) : undefined;
      if (!zoneId || !overlay || !dxf || !zone) {
        write(empty);
        if (editSet) {
          // Un-hide the originals and bake the finished offset in one pass.
          clearHiddenStates(editSet);
          editSet = null;
          sync();
        }
        return;
      }
      const mapper = resolveMapper(overlay, dxf);
      if (!mapper?.hasPageSpace) { write(empty); return; }

      // FAST PATH: same selection, only its offset changed — re-transform the
      // handful of captured features; the big sources are not touched at all.
      if (editSet && editSet.zoneId === zoneId) {
        write(renderEditSet(editSet, zone));
        return;
      }
      // Switching selection: release the previous one first.
      if (editSet) { clearHiddenStates(editSet); editSet = null; }

      // Selection changed: scan ONCE, lift the captured features out, and let
      // the big sources rebuild without them. Every later nudge is then
      // proportional to the selection, not to the layer.
      const items: EditSet['items'] = [];
      const excluded = new Map<string, Set<number>>();
      const [zx0, zy0, zx1, zy1] = zoneBBox(zone);
      const regSig = dxf.registration?.kind === 'layout' ? dxf.registration.matrix.join(',') : 'x';
      for (const index of dxf.picked ?? []) {
        const raw = await fetchRawLayer(dxf.outPrefix, index).catch(() => null);
        if (disposed) return;
        if (!raw) continue;
        const sourceId = dxfSourceId(overlay.id, index);
        const bx = pageBBoxes(`${dxf.outPrefix}${index}`, regSig, raw, mapper);
        const idxs = new Set<number>();
        for (let i = 0; i < raw.features.length; i++) {
          // Cheap rejection first: no bbox overlap, no capture. This is what
          // turns a multi-second scan into a few milliseconds.
          if (bx[i * 4] > zx1 || bx[i * 4 + 2] < zx0 || bx[i * 4 + 1] > zy1 || bx[i * 4 + 3] < zy0) continue;
          const f = raw.features[i];
          const isPolygon = f.geometry.type === 'Polygon';
          const rings = isPolygon
            ? (f.geometry as GeoJSON.Polygon).coordinates
            : [(f.geometry as GeoJSON.LineString).coordinates];
          const pageRings = rings.map((r) => r.map(([x, y]) => mapper.toRawPage(x, y)));
          // Capture is asked directly: a freshly drawn zone has delta (0,0)
          // and must still show — and hold — its contents.
          if (!zoneCaptures(zone, pageRings[0])) continue;
          idxs.add(i);
          items.push({ rings: pageRings, isPolygon, props: { f: f.properties?.f ?? 0 } });
          if (items.length >= 8000) break; // an edit selection, not a re-render
        }
        if (idxs.size) excluded.set(sourceId, idxs);
        if (items.length >= 8000) break;
      }
      editSet = { zoneId, excluded, items, mapper };
      applyHiddenStates(editSet);
      write(renderEditSet(editSet, zone));
      // NO sync() here: the originals are hidden by feature-state, so the big
      // sources keep their existing data untouched.
    }

    /** Hide the lifted originals; their moved copies live in the edit source. */
    function applyHiddenStates(es: EditSet) {
      for (const [sourceId, idxs] of es.excluded) {
        if (!map!.getSource(sourceId)) continue;
        for (const i of idxs) {
          try { map!.setFeatureState({ source: sourceId, id: i }, { hidden: true }); } catch { /* not ready */ }
        }
      }
    }

    function clearHiddenStates(es: EditSet | null) {
      if (!es) return;
      for (const sourceId of es.excluded.keys()) {
        try {
          if (map!.getSource(sourceId)) map!.removeFeatureState({ source: sourceId });
        } catch { /* source gone */ }
      }
    }

    /** Map the edit set through the zone's current offset — the only work a
     *  nudge or a drag commit has to do. */
    function renderEditSet(es: EditSet, zone: DxfZone): GeoJSON.FeatureCollection {
      const { mapper } = es;
      return {
        type: 'FeatureCollection',
        features: es.items.map((it) => {
          const rings = it.rings.map((r) => r.map(([x, y]) => {
            const [ax, ay] = mapper.applyMacro(x + zone.dx, y + zone.dy);
            return mapper.pageToMap(ax, ay);
          }));
          return {
            type: 'Feature' as const,
            properties: it.props,
            geometry: it.isPolygon
              ? { type: 'Polygon' as const, coordinates: rings }
              : { type: 'LineString' as const, coordinates: rings[0] },
          };
        }),
      };
    }

    sync();
    void syncHighlight();

    const unsubMap = useMapStore.subscribe((s, p) => {
      if (s.dxfSelectedZoneId !== p.dxfSelectedZoneId || s.dxfZoneOverlayId !== p.dxfZoneOverlayId) {
        void syncHighlight();
      }
      // Legend Vectors checkbox: add/remove that plan's sources.
      if (s.hiddenVectorOverlayIds !== p.hiddenVectorOverlayIds) {
        sync();
      }
    });

    const unsub = useProjectStore.subscribe((state, prevState) => {
      // overlays: picked/visibility/registration/bounds. layouts: the
      // transform a layout registration renders through.
      if (state.data.overlays !== prevState.data.overlays
          || state.data.siteLayouts !== prevState.data.siteLayouts
          || state.data.siteLayout !== prevState.data.siteLayout) {
        sync();
        void syncHighlight();
      }
    });

    return () => {
      disposed = true;
      unsub();
      unsubMap();
      clearHiddenStates(editSet);
      editSet = null;
      for (const sourceId of [...rendered.keys()]) removeEntry(sourceId);
      try {
        if (map.getLayer(DXF_HL_LINE)) map.removeLayer(DXF_HL_LINE);
        if (map.getLayer(DXF_HL_FILL)) map.removeLayer(DXF_HL_FILL);
        if (map.getSource(DXF_HL_SRC)) map.removeSource(DXF_HL_SRC);
      } catch { /* tearing down */ }
    };
  }, [isMapReady, mapRef]);
}
